import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ContratoFormalEtapa } from '@/components/ContratoFormalEtapa';
import type { ResponsavelData } from '@/services/conexaService';

// =============================================================================
// /contrato-formal/:adhesionId — Pagina dedicada do contrato formal opcional
// =============================================================================
// Substituiu o que era a Etapa 5 inline em /registro-cliente. O cliente chega
// aqui ao clicar no CTA "Gerar contrato formal" em /cadastro-sucesso pos-aceite.
//
// Dados ricos (sócios, responsáveis) vem via navigate state (setados em
// RegistroCliente.tsx no onAccepted). companyId tem fallback pra sessionStorage
// pra sobreviver F5 dentro do mesmo navegador.
//
// Limitação conhecida: deep-link direto (compartilhar URL com terceiro, ou
// abrir em outro navegador) NÃO funciona porque não temos endpoint pra fetch
// da Adhesion. Fica como erro amigável + sugestão de voltar pelo fluxo normal.
// Quando houver demanda real, plugar fetch da Adhesion via radarGetAdhesion
// (não existe ainda — fora do escopo desta refator).
// =============================================================================

interface ContratoFormalNavState {
  companyId?: string;
  socios?: ResponsavelData[];
  responsavelOperacional?: { nome: string; email: string };
  responsavelFinanceiro?: { nome: string; email: string };
}

export default function ContratoFormalPage() {
  const { adhesionId = '' } = useParams<{ adhesionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as ContratoFormalNavState | null) || null;

  // companyId resolve em ordem: navigate state > sessionStorage (fallback F5)
  const companyId = useMemo(
    () => state?.companyId || sessionStorage.getItem('companyId') || '',
    [state],
  );

  // Sem contexto mínimo (adhesionId + companyId) ou sem dados ricos no state,
  // mostra erro amigável. Não tentamos refetch porque hoje não temos endpoint
  // pra isso — adicionar quando precisar suportar deep-link.
  const hasFullContext =
    !!adhesionId && !!companyId && Array.isArray(state?.socios) && !!state?.responsavelOperacional;

  if (!hasFullContext) {
    return (
      <div className="min-h-screen bg-muted/30">
        <header className="py-4 bg-white border-b border-border">
          <div className="container px-4 mx-auto flex items-center">
            <img src="/brand/logo-secundario.svg" alt="dgenny" className="h-9" />
          </div>
        </header>

        <main className="container px-4 mx-auto py-12 md:py-16">
          <div className="max-w-2xl mx-auto">
            <Card className="p-10 border-border shadow-sm rounded-3xl text-center">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center">
                  <AlertTriangle size={44} className="text-amber-600" />
                </div>
              </div>
              <h1 className="font-display text-2xl md:text-3xl font-extrabold text-brand-azul mb-3">
                Sessão expirada
              </h1>
              <p className="text-brand-azul/75 mb-8 leading-relaxed">
                Não foi possível recuperar os dados da adesão. Isso acontece quando
                a página é aberta em outro navegador ou recarregada depois de
                muito tempo. Para gerar o contrato formal, retorne à tela de
                sucesso do cadastro.
              </p>
              <p className="text-sm text-brand-azul/55 mb-6">
                Se você fechou a aba sem completar o contrato formal, entre em
                contato com nosso time pelo e-mail abaixo.
              </p>
              <div className="bg-muted/60 rounded-2xl p-5 mb-8 inline-block">
                <p className="text-sm text-brand-azul/60">Suporte</p>
                <p className="text-brand-azul font-semibold mt-1">compre.ai@dgenny.com.br</p>
              </div>
              <div className="flex justify-center gap-3">
                <Button variant="outline" onClick={() => navigate('/cadastro-sucesso')}>
                  Voltar para a tela de sucesso
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  className="bg-brand-roxo hover:bg-brand-roxo/90 text-white"
                >
                  Voltar para a home
                </Button>
              </div>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="py-4 bg-white border-b border-border">
        <div className="container px-4 mx-auto flex items-center">
          <img src="/brand/logo-secundario.svg" alt="dgenny" className="h-9" />
        </div>
      </header>

      <main className="container px-4 mx-auto py-8 md:py-12">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 shadow-lg border-border rounded-2xl mb-6 relative overflow-hidden">
            <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-brand-roxo to-brand-rosa" aria-hidden />
            <ContratoFormalEtapa
              adhesionId={adhesionId}
              companyId={companyId}
              socios={state.socios || []}
              responsavelOperacional={state.responsavelOperacional!}
              responsavelFinanceiro={
                state.responsavelFinanceiro || { nome: '', email: '' }
              }
              onBack={() => navigate('/cadastro-sucesso')}
              onCompleted={() =>
                navigate('/cadastro-sucesso', {
                  state: { adhesionId, companyId, hasDgennyProposta: true, contractDraftCompleted: true },
                })
              }
            />
          </Card>
        </div>
      </main>
    </div>
  );
}
